import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { filter } from 'rxjs/operators';
import { CoreModalService } from '../core-modal/core-modal.service';
import { ModalType } from './modal.enum';
import { SdCoreModalComponent } from '../core-modal/core-modal.component';
import { BrandsEnum } from '../../../_enums/brands.enum';
import { PositionEnum } from '../../../_enums/position.enum';
import { RxjsComponent } from '../../../utils/rxjs.component';

@Component({
  selector: 'sd-modal, [sd-modal]',
  templateUrl: './modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CdkOverlayOrigin, SdCoreModalComponent, NgTemplateOutlet]
})
export class SdModalComponent extends RxjsComponent implements OnInit {
  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private coreModalService: CoreModalService,
  ) {
    super(changeDetectorRef);
  }

  _type: ModalType = ModalType.modal;

  @Input()
  brand: BrandsEnum;

  @Input()
  disabledAnimation = true;

  /**
   * Allows to display an overlay behind the modal
   */
  @Input()
  backdrop: boolean;

  @Input()
  clickToOpen = true;

  /**
   * Allows to close the modal when clicking next to it
   */
  @Input()
  clickToClose = true;

  /**
   * Allow to define a custom class for the modal in the CDK overlay
   */
  @Input()
  modalCustomClass = '';

  /**
   * Allow to define a custom ID for the modal in the CDK overlay
   */
  @Input()
  modalCustomId = '';

  /**
   * Modal's state (closed or opened)
   */
  @Input()
  isOpened = false;

  @Input()
  disabled = false;

  @Output()
  isOpenedChange = new EventEmitter<boolean>();

  @Input()
  focus = true;

  /**
   * Defines the position of the modal after opening: 'bottom', 'top', 'left', 'right', 'bottom-left'.
   */
  @Input()
  position:
    | PositionEnum.left
    | PositionEnum.top
    | PositionEnum.right
    | PositionEnum.bottom
    | PositionEnum.bottomLeft;

  /**
   * Defines the type of the modal and assigns a related class to it
   */
  @Input()
  set type(type: ModalType) {
    if (!type) {
      throw new Error(
        `Le type que vous passez est ${type} pensez à utilisez [type]="'dropdown'" \n
        voir les types ${JSON.stringify(Object.keys(ModalType))}`,
      );
    }
    this._type = type;
    this.modalClass = 'c--' + type;
  }

  get type() {
    return this._type;
  }

  @HostBinding('class') modalClass = '';

  @ViewChild('coreModal')
  coreModal: SdCoreModalComponent;

  /**
   * Manage clicks on the document to show / hide the modal
   * according to the past parameters (clickToClose) and the target of the click
   */
  onClick() {
    this.openedChange(!this.isOpened);
  }

  ngOnInit() {
    this.register(
      this.coreModalService.close$.subscribe(() => this.openedChange(false)),
    );

    this.register(
      this.coreModalService.open$
        .pipe(filter((id) => id === this.modalCustomClass))
        .subscribe(() => this.openedChange(true)),
    );
  }

  openedChange(isOpened: boolean) {
    if (!this.disabled) {
      this.isOpened = isOpened;
      this.isOpenedChange.emit(isOpened);
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Allow to update coreModal position
   */
  updatePosition() {
    this.coreModal.updatePosition();
  }
}
